import { v4 as uuidv4 } from 'uuid';
import { parseToolUseMessage } from '../../chat_window/utils/parseToolMessage';
import { useAuthStore } from '@/stores/authStore';
// WebSocket import 제거하고 브라우저 내장 WebSocket 사용

// WebSocket 이벤트 타입 정의
interface MessageEventLike {
  readonly data: string | ArrayBuffer | Blob | ArrayBufferView;
  readonly type: string;
  readonly target: EventTarget | null;
}

interface CloseEventLike {
  readonly code: number;
  readonly reason: string;
  readonly wasClean: boolean;
  readonly type: string;
  readonly target: EventTarget | null;
}

// WebSocket 타입 정의
interface WebSocketLike {
  readonly readyState: number;
  onopen: ((this: WebSocket, ev: Event) => unknown) | null;
  onclose: ((this: WebSocket, ev: CloseEvent) => unknown) | null;
  onerror: ((this: WebSocket, ev: Event | ErrorEvent) => unknown) | null;
  onmessage: ((this: WebSocket, ev: MessageEvent) => unknown) | null;
  close(code?: number, reason?: string): void;
  send(data: string | ArrayBufferLike | Blob | ArrayBufferView): void;
}

// WebSocket 상태 상수 정의
const WEBSOCKET_STATES = {
  CONNECTING: 0,
  OPEN: 1,
  CLOSING: 2,
  CLOSED: 3
};

interface AiRequestOptions {
  query: string;
  userId?: string;
  userName?: string;
  language?: string;
  timezone?: string;
  sessionId?: string;
  toolsString?: string;
  additionalData?: Record<string, unknown>;
  onMessage?: (message: MessageContent) => void;
  onTextMessage?: (message: string, isNewMessage?: boolean) => void;
  onToolUseMessage?: (message: string) => void;
  onToolUseStart?: (toolId: string, toolName: string, input: Record<string, unknown>) => void;
  onToolUseComplete?: (toolName: string) => void;
  onComplete?: (fullResponse: string) => void;
  onError?: (error: string) => void;
  onOpen?: () => void;
  onClose?: () => void;
}

interface WebSocketMessage {
    query: string;
    user_id?: string;
    user_name?: string;
    language?: string;
    timezone?: string;
    request_id: string;
    toolsString?: string;
    data?: Record<string, unknown>;
}

// 서버 응답의 메시지 콘텐츠 타입
type MessageContent = TextContent | ToolUseContent;

interface TextContent {
  type: 'text';
  text: string;
  index: number;
}

interface ToolUseContent {
  type: 'tool_use';
  id?: string;
  name?: string;
  input?: Record<string, unknown>;
  partial_json: string;
  index: number;
}

// JSON 파싱 결과에 대한 인터페이스 정의
interface ToolUseResult {
  status?: string;
  [key: string]: unknown;
}

// 서버에서 받는 메시지 타입 정의
interface ServerResponse {
  node?: string;
  content?: string | MessageContent[];
  register_id?: string;
  session_id?: string;
  type?: string;  // 'chunk'
  done?: boolean;
  error?: string;
  success?: boolean;
  results?: { rows: unknown[] }[];
  data?: unknown[];
  rowcount?: number;
  status?: string;
}

// 도구 정보 인터페이스
interface ToolInfo {
  name: string;
  input?: Record<string, unknown>;
  isCompleted?: boolean;
  specialType?: string; // 노트 검색 결과와 같은 특수 도구 타입을 위한 속성
}

interface AiUICallbacks {
  onTextUpdate?: (text: string, isNewMessage?: boolean) => void;
  onToolUseReceived?: (toolUseData: string) => void;
  onToolUseStarted?: (toolId: string, toolName: string, input: Record<string, unknown>) => void;
  onToolUseCompleted?: (toolId: string, toolName: string) => void;
  onStateUpdate?: (activeTools: ToolInfo[]) => void;  // 활성 도구 상태 업데이트 콜백 추가
}

class AiRequestManager {
  // WebSocket 타입을 명시적 인터페이스로 지정
  private socket: WebSocketLike | null = null;
  private isConnected = false;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectTimeout = 1000;
  private messageQueue: WebSocketMessage[] = [];
  private responseBuffer = '';
  private textResponseBuffer = '';
  private toolUseResponseBuffer = '';
  private activeRequestId: string | null = null;
  private sessionId: string;
  private activeTools = new Set<string>(); // 현재 활성화된 도구 ID 목록
  private toolJsonBuffers = new Map<string, string>(); // 각 도구별 JSON 누적 버퍼
  private lastToolName: string | null = null; // 마지막으로 감지된 도구 이름
  private uiCallbacks: AiUICallbacks = {}; // UI 업데이트를 위한 콜백
  private activeToolsInfo = new Map<string, ToolInfo>(); // 활성 도구 정보 저장
  private activeToolsList: ToolInfo[] = []; // 활성 도구 목록 (배열 형태)
  private hasUsedTool = false; // 도구가 사용된 적이 있는지 여부
  private isFirstChunkAfterTool = false; // 도구 사용 후 첫 번째 텍스트 청크인지 여부
  private wsBaseUrl: string; // WebSocket 기본 URL
  
  private callbacks = new Map<string, {
    onMessage?: (message: MessageContent) => void;
    onTextMessage?: (message: string, isNewMessage?: boolean) => void;
    onToolUseMessage?: (message: string) => void;
    onToolUseStart?: (toolId: string, toolName: string, input: Record<string, unknown>) => void;
    onToolUseComplete?: (toolName: string) => void;
    onComplete?: (fullResponse: string) => void;
    onError?: (error: string) => void;
  }>();

  constructor(sessionId?: string) {
    // 세션 ID가 제공되지 않은 경우 UUID를 사용하여 랜덤하게 생성
    this.sessionId = sessionId || uuidv4();
    
    // 환경에 따른 WebSocket 기본 URL 설정
    const baseUrl = this.getWebSocketBaseUrl();
    this.wsBaseUrl = baseUrl;
    console.log(`[AiRequestManager] WebSocket 기본 URL 설정: ${baseUrl}`);
    console.log(`[AiRequestManager] 세션 ID 설정: ${this.sessionId}`);
  }
  
  /**
   * 환경에 따른 WebSocket 기본 URL을 반환
   */
  private getWebSocketBaseUrl(): string {
    // 환경 변수에서 API URL 가져오기 (없으면 기본값 사용)
    const apiUrl = (import.meta.env.VITE_API_URL as string) || 'https://works.orblit.space/api';
    
    // WebSocket URL로 변환 (http -> ws, https -> wss)
    let wsUrl = apiUrl.replace(/^http/, 'ws');
    
    // URL이 이미 /api로 끝나는지 확인, 아니면 추가
    if (!wsUrl.endsWith('/api')) {
      wsUrl = wsUrl.endsWith('/') ? `${wsUrl}api` : `${wsUrl}/api`;
    }
    
    // WebSocket 경로 반환
    return `${wsUrl}/mcp-ws`;
  }

  /**
   * 웹소켓 연결을 초기화합니다
   */
  public connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.isConnected && this.socket && this.socket.readyState === WEBSOCKET_STATES.OPEN) {
        console.log('WebSocket already connected and open, reusing connection');
        resolve();
        return;
      }

      // 이미 연결 중이면 기존 소켓을 정리
      if (this.socket) {
        try {
          console.log('Cleaning up existing socket connection');
          this.socket.onclose = null;
          this.socket.onerror = null;
          this.socket.onmessage = null;
          this.socket.onopen = null;
          this.socket.close();
          this.socket = null;
        } catch (err) {
          console.error('Error cleaning up socket:', err);
        }
      }

      try {
        // 세션 ID로 WebSocket URL 구성
        const wsUrl = `${this.wsBaseUrl}/${this.sessionId}`;
        console.log(`[AiRequestManager] WebSocket 연결 시도: ${wsUrl}`);
        
        // 브라우저 내장 WebSocket API 사용
        const socket = new WebSocket(wsUrl);
        
        // 연결 타임아웃 설정 (10초)
        const connectionTimeout = setTimeout(() => {
          console.error('WebSocket connection timeout');
          if (socket.readyState !== WEBSOCKET_STATES.OPEN) {
            socket.close();
            reject(new Error('WebSocket 연결 시간이 초과되었습니다. 네트워크 상태를 확인해 주세요.'));
          }
        }, 10000);
        
        this.socket = socket;

        socket.onopen = () => {
          console.log('[AiRequestManager] WebSocket 연결 성공');
          clearTimeout(connectionTimeout);
          this.isConnected = true;
          this.reconnectAttempts = 0;
          
          // 대기 중인 메시지가 있으면 모두 전송
          while (this.messageQueue.length > 0) {
            const message = this.messageQueue.shift();
            if (message) this.sendMessage(message);
          }
          
          resolve();
        };

        socket.onclose = (event) => {
          // CloseEvent에서 안전하게 속성 접근
          const code = event?.code ?? 0;
          const reason = event?.reason ?? '';
          
          console.log(`[AiRequestManager] WebSocket 연결 종료: 코드 ${code} ${reason ? '사유: ' + reason : ''}`);
          this.isConnected = false;
          
          // 자동 재연결 시도
          if (this.reconnectAttempts < this.maxReconnectAttempts) {
            this.reconnectAttempts++;
            console.log(`[AiRequestManager] 재연결 시도 중 (${this.reconnectAttempts}/${this.maxReconnectAttempts})...`);
            
            // 지수 백오프로 재연결 시간 증가 (1초, 2초, 4초, 8초, 16초)
            const backoffTime = Math.min(30000, this.reconnectTimeout * Math.pow(2, this.reconnectAttempts - 1));
            console.log(`[AiRequestManager] ${backoffTime}ms 후 재연결 시도`);
            
            setTimeout(() => {
              this.connect().catch((err: Error) => {
                console.error(`[AiRequestManager] 재연결 시도 ${this.reconnectAttempts} 실패:`, err);
              });
            }, backoffTime);
          } else {
            console.error('[AiRequestManager] 최대 재연결 시도 횟수에 도달했습니다');
            this.callbacks.forEach(callback => {
              if (callback.onError) {
                callback.onError('WebSocket 연결이 끊어졌으며 다시 연결할 수 없습니다. 페이지를 새로고침하거나 나중에 다시 시도해 주세요.');
              }
            });
            reject(new Error('최대 재연결 시도 횟수에 도달했습니다'));
          }
        };

        socket.onerror = (error) => {
          console.error('[AiRequestManager] WebSocket 오류 발생:', error);
          // 연결 타임아웃 취소
          clearTimeout(connectionTimeout);
          
          // 오류가 발생한 경우 연결 상태 확인
          if (!this.isConnected) {
            reject(new Error('WebSocket 연결 오류가 발생했습니다. 네트워크 상태를 확인해 주세요.'));
          }
          
          // 오류 콜백 호출
          this.callbacks.forEach(callback => {
            if (callback.onError) {
              callback.onError('WebSocket 연결 오류가 발생했습니다. 연결이 불안정할 수 있습니다.');
            }
          });
        };

        socket.onmessage = (event) => {
          try {
            // 웹소켓에서 받은 문자열 데이터를 안전하게 추출
            let rawData = '';
            
            if (event?.data) {
              if (typeof event.data === 'string') {
                rawData = event.data;
              } else if (event.data instanceof Blob) {
                // Blob 데이터는 비동기 처리가 필요하지만 여기서는 로그만 출력
                console.log('[AiRequest] Blob 데이터 수신 (동기적으로 처리하지 않음)');
                return;
              } else if (event.data instanceof ArrayBuffer || ArrayBuffer.isView(event.data)) {
                // ArrayBuffer나 TypedArray 처리
                const decoder = new TextDecoder();
                rawData = decoder.decode(
                  event.data instanceof ArrayBuffer ? event.data : event.data.buffer
                );
              } else {
                // 기타 타입 처리 (안전하게)
                console.log('[AiRequest] 알 수 없는 데이터 타입 수신:', typeof event.data);
                return;
              }
            }
            
            // 문자열이 비어있지 않은 경우만 처리
            if (rawData) {
              // ServerResponse 타입으로 변환
              const data = JSON.parse(rawData) as ServerResponse;
              this.handleMessage(data);
            }
          } catch (error) {
            console.error('Error parsing message:', error);
          }
        };
      } catch (error) {
        console.error('WebSocket initialization error:', error);
        reject(new Error(String(error)));
      }
    });
  }

  /**
   * 웹소켓 연결을 종료합니다
   */
  public disconnect(): void {
    if (this.socket) {
      try {
        this.socket.close();
        this.socket = null;
        this.isConnected = false;
      } catch (err) {
        console.error('Error closing socket:', err);
      }
    }
  }

  /**
   * AI에 요청을 보냅니다
   */
  public async sendRequest(options: AiRequestOptions): Promise<string> {
    const requestId = uuidv4();
    
    // 콜백 등록
    this.callbacks.set(requestId, {
      onMessage: options.onMessage,
      onTextMessage: options.onTextMessage,
      onToolUseMessage: options.onToolUseMessage,
      onToolUseStart: options.onToolUseStart,
      onToolUseComplete: options.onToolUseComplete,
      onComplete: options.onComplete,
      onError: options.onError
    });

    // 메시지 준비
    const message: WebSocketMessage = {
      query: options.query,
      request_id: requestId,
      user_id: options.userId,
      user_name: options.userName,
      timezone: options.timezone,
      language: options.language,
      data: options.additionalData,
      toolsString: options.toolsString
    };
    console.log('전송된 메시지:', message);

    // 응답 버퍼 초기화
    this.responseBuffer = '';
    this.textResponseBuffer = '';
    this.toolUseResponseBuffer = '';
    this.activeTools.clear();
    this.toolJsonBuffers.clear();
    this.lastToolName = null;
    this.hasUsedTool = false; // 대화 시작 시 도구 사용 플래그 초기화
    this.isFirstChunkAfterTool = false; // 도구 사용 후 첫 번째 텍스트 청크인지 여부

    // 웹소켓 연결 확인
    if (!this.isConnected) {
      try {
        await this.connect();
      } catch (error) {
        const errorMsg = 'Failed to connect to WebSocket server';
        if (options.onError) options.onError(errorMsg);
        throw new Error(errorMsg);
      }
    }

    return new Promise((resolve, reject) => {
      // 응답 완료 시 호출될 콜백 확장
      const originalOnComplete = this.callbacks.get(requestId)?.onComplete;
      this.callbacks.set(requestId, {
        ...this.callbacks.get(requestId),
        onComplete: (response) => {
          if (originalOnComplete) originalOnComplete(response);
          resolve(response);
          this.callbacks.delete(requestId);
        },
        onError: (error) => {
          if (options.onError) options.onError(error);
          reject(new Error(error));
          this.callbacks.delete(requestId);
        }
      });

      // 메시지 전송
      this.sendMessage(message);
      this.activeRequestId = requestId;
    });
  }

  /**
   * 웹소켓을 통해 메시지를 전송합니다
   */
  private sendMessage(message: WebSocketMessage): void {
    try {
      // readyState 값을 직접 비교
      if (this.isConnected && this.socket && this.socket.readyState === WEBSOCKET_STATES.OPEN) {
        this.socket.send(JSON.stringify(message));
      } else {
        this.messageQueue.push(message);
        
        // 연결이 안된 상태라면 연결 시도
        if (!this.isConnected) {
          this.connect().catch((error: Error) => {
            console.error('Connection error:', error);
            const callback = this.callbacks.get(message.request_id);
            if (callback?.onError) {
              callback.onError('Failed to connect to WebSocket server');
            }
          });
        }
      }
    } catch (error) {
      console.error('Error sending message:', error);
      const callback = this.callbacks.get(message.request_id);
      if (callback?.onError) {
        callback.onError(`Failed to send message: ${error}`);
      }
    }
  }

  /**
   * 수신된 메시지를 처리합니다
   */
  private handleMessage(data: ServerResponse): void {
    // 원본 데이터 로깅 (청크 확인용)
    console.log('[AiRequest] 원본 데이터 수신:', JSON.stringify(data));
    
    // 에러 메시지 처리
    if (data.error) {
      console.error('Error from server:', data.error);
      const requestId = data.register_id || this.activeRequestId;
      if (requestId) {
        const callback = this.callbacks.get(requestId);
        if (callback?.onError) {
          callback.onError(data.error);
        }
      }
      return;
    }

    // 메시지에 register_id가 없으면 현재 활성화된 요청 ID 사용
    const requestId = data.register_id || this.activeRequestId;
    if (!requestId) return;

    const callback = this.callbacks.get(requestId);
    if (!callback) return;

    // 서버에서 온 content가 배열인 경우 (메시지 타입별 처리)
    if (Array.isArray(data.content)) {
      console.log('[AiRequest] 배열 콘텐츠 수신:', JSON.stringify(data.content));
      data.content.forEach((content) => {
        if (content.type === 'text' && 'text' in content) {
          // 텍스트 메시지 처리
          const textContent = content;
          console.log('[AiRequest] 텍스트 청크 수신:', JSON.stringify(textContent));
          
          // 텍스트 응답 처리 로직 (활성화)
          this.responseBuffer += textContent.text;
          this.textResponseBuffer += textContent.text;
          
          console.log('[AiRequest] 텍스트 응답 추가:', textContent.text);
          
          // 텍스트 메시지가 도착해도 도구 완료 처리하지 않음
          // 여러 번의 텍스트-도구-텍스트 시퀀스를 처리하기 위함
          if (this.activeTools.size > 0) {
            console.log('[AiRequest] 문자열 텍스트 응답, 활성 도구 상태 유지:', {
              activeToolsCount: this.activeTools.size,
              lastToolName: this.lastToolName
            });
          }
          
          // 도구 사용 후에만 첫번째 청크 플래그 확인
          let isNewMessage = false;
          if (this.hasUsedTool) {
            isNewMessage = this.isFirstChunkAfterTool;
            if (isNewMessage) {
              console.log('[AiRequest] 도구 사용 후 첫 번째 텍스트 청크, 새 메시지로 표시');
              this.isFirstChunkAfterTool = false; // 첫 번째 청크 처리 후 플래그 해제
            }
          }
          
          if (callback.onTextMessage) {
            callback.onTextMessage(textContent.text, isNewMessage);
          }
          if (callback.onMessage) {
            callback.onMessage(textContent);
          }
          
        } else if (content.type === 'tool_use') {
          // 도구 사용 메시지 처리
          const toolUseContent = content;
          console.log('[AiRequest] 도구 사용 청크 수신:', JSON.stringify(toolUseContent));
          
          // 도구 사용 처리 로직 (활성화)
          // 도구 정보 로깅
          console.log('[AiRequest] 도구 사용 메시지 처리 시작:', {
            id: toolUseContent.id,
            name: toolUseContent.name, 
            type: toolUseContent.type,
            hasPartialJson: !!toolUseContent.partial_json,
            partialJsonLength: toolUseContent.partial_json?.length || 0
          });
          
          // 첫 번째 chunk에서 도구 정보가 담겨있는 경우
          if (toolUseContent.name) {
            // 도구 이름 감지 시 저장 (이후 빈 JSON이나 부분 JSON에 사용)
            console.log('[AiRequest] 도구 이름 감지 및 저장:', toolUseContent.name);
            this.lastToolName = toolUseContent.name;
            
            // 도구 ID가 있으면 저장
            if (toolUseContent.id && !this.activeTools.has(toolUseContent.id)) {
              console.log('[AiRequest] 새 도구 감지:', toolUseContent.name, toolUseContent.id);
              this.activeTools.add(toolUseContent.id);
              
              // 도구별 JSON 버퍼 초기화
              this.toolJsonBuffers.set(toolUseContent.id, '');
              
              // 도구 사용 시작 알림
              if (callback.onToolUseStart) {
                callback.onToolUseStart(
                  toolUseContent.id,
                  toolUseContent.name,
                  toolUseContent.input || {}
                );
                this.hasUsedTool = true; // 도구 사용 플래그 설정
                this.isFirstChunkAfterTool = true; // 도구 사용 후 첫 번째 텍스트 청크 플래그 설정
              }
            }
          } else if (toolUseContent.id) {
            // ID만 있고 이름이 없는 경우 (이전에 감지된 도구일 가능성)
            if (this.lastToolName) {
              console.log('[AiRequest] ID만 있고 이름이 없는 도구 메시지. 마지막 도구 이름 사용:', this.lastToolName);
              
              // 이미 활성화된 도구가 아니면 시작 이벤트 발생
              if (!this.activeTools.has(toolUseContent.id)) {
                console.log('[AiRequest] 활성화되지 않은 도구 ID 발견, 시작 이벤트 발생:', toolUseContent.id);
                this.activeTools.add(toolUseContent.id);
                
                if (callback.onToolUseStart) {
                  callback.onToolUseStart(
                    toolUseContent.id,
                    this.lastToolName,
                    toolUseContent.input || {}
                  );
                  this.hasUsedTool = true; // 도구 사용 플래그 설정
                  this.isFirstChunkAfterTool = true; // 도구 사용 후 첫 번째 텍스트 청크 플래그 설정
                }
              }
            }
          }
          
          // 부분 JSON 처리 (빈 문자열이 아닌 경우만)
          if (toolUseContent.partial_json && toolUseContent.partial_json.trim() !== '') {
            // 전체 도구 사용 응답 버퍼에 추가
            this.toolUseResponseBuffer += toolUseContent.partial_json;
            
            // ID가 있는 경우 도구별 버퍼에도 추가
            if (toolUseContent.id) {
              this.toolJsonBuffers.set(
                toolUseContent.id, 
                (this.toolJsonBuffers.get(toolUseContent.id) || '') + toolUseContent.partial_json
              );
            }
          }
          
          // 원시 JSON 데이터 로깅
          console.log('[AiRequest] 도구 사용 원시 데이터:', toolUseContent);
          
          // 누적된 JSON 파싱 시도 (ID가 있는 경우)
          if (toolUseContent.id) {
            const accumulatedJson = this.toolJsonBuffers.get(toolUseContent.id) || '';
            if (accumulatedJson.trim() !== '') {
              try {
                // 누적된 JSON을 파싱하여 각종 정보 추출
                const toolData = this.tryParseJson(accumulatedJson);
                
                if (toolData) {
                  // 도구 이름 업데이트 필요한 경우
                  if ('name' in toolData && typeof toolData.name === 'string' && !toolUseContent.name) {
                    console.log('[AiRequest] 도구 이름 업데이트:', toolData.name);
                    this.lastToolName = toolData.name;
                    
                    // 이벤트를 아직 발생시키지 않았다면 시작 이벤트 발생
                    if (!this.activeTools.has(toolUseContent.id)) {
                      this.activeTools.add(toolUseContent.id);
                      
                      if (callback.onToolUseStart) {
                        const toolInput = typeof toolData.input === 'object' && toolData.input !== null 
                          ? toolData.input as Record<string, unknown>
                          : toolUseContent.input || {};
                          
                        callback.onToolUseStart(
                          toolUseContent.id,
                          toolData.name,
                          toolInput
                        );
                        this.hasUsedTool = true; // 도구 사용 플래그 설정
                        this.isFirstChunkAfterTool = true; // 도구 사용 후 첫 번째 텍스트 청크 플래그 설정
                      }
                    }
                  }
                  
                  // 도구 완료 상태 확인 - 명시적으로 완료된 경우에만 처리
                  if ('status' in toolData && toolData.status === 'completed') {
                    // 완료할 도구 이름 결정
                    const toolName = toolUseContent.name || 
                      (typeof toolData.name === 'string' ? toolData.name : this.lastToolName || 'unknown_tool');
                      
                    console.log('[AiRequest] 도구 사용 완료 감지:', toolName, {
                      data: toolData,
                      status: toolData.status,
                      toolName,
                      id: toolUseContent.id
                    });
                    
                    if (callback.onToolUseComplete && toolName) {
                      console.log('[AiRequest] 도구 사용 완료 콜백 호출:', toolName);
                      callback.onToolUseComplete(toolName);
                      // 도구 상태 초기화 (JSON 파싱은 계속 시도)
                      this.activeTools.delete(toolUseContent.id);
                    }
                  }
                }
              } catch (error) {
                // 누적된 JSON 파싱 실패는 로그만 출력하고 계속 진행
                console.log('[AiRequest] 누적 JSON 파싱 실패 (정상):', error);
              }
            }
          }
          
          // 부분 JSON 조각의 모든 조합을 시도 (완전히 새로운 JSON일 경우를 위해)
          try {
            // 부분 JSON이 유효하면 해석
            if (toolUseContent.partial_json && toolUseContent.partial_json.trim() !== '') {
              const partialJson = toolUseContent.partial_json;
              
              // 전체 도구 사용 응답 버퍼에 추가
              this.toolUseResponseBuffer += partialJson;
              
              // 부분 JSON 직접 파싱 시도
              const toolData = this.tryParseJson(partialJson);
              
              // 부분 JSON에서 도구 이름 감지
              if (toolData && 'name' in toolData && typeof toolData.name === 'string') {
                this.lastToolName = toolData.name;
                console.log('[AiRequest] 부분 JSON에서 도구 이름 감지:', toolData.name);
              }
              
              // 콜백 호출
              if (callback.onToolUseMessage) {
                callback.onToolUseMessage(partialJson);
              }
            } else if (callback.onToolUseMessage && toolUseContent.partial_json !== undefined) {
              // 빈 JSON도 콜백은 호출
              callback.onToolUseMessage(toolUseContent.partial_json);
            }
          } catch (error) {
            console.log('[AiRequest] 부분 JSON 처리 오류:', error);
          }
          
          if (callback.onMessage) {
            callback.onMessage(toolUseContent);
          }
        }
      });
    } else if (typeof data.content === 'string') {
      console.log('[AiRequest] 문자열 콘텐츠 수신:', data.content);
      
      // JSON 형식 결과에 note_id가 있는지 확인 (노트 검색 결과인 경우)
      let isNoteResult = false;
      // 시각화 데이터 결과인지 확인하는 플래그 추가
      let isVisualizationResult = false;
      let visualizationData = null;
      
      try {
        // 먼저 parseToolUseMessage 함수로 도구 사용 메시지인지 확인
        const toolInfo = parseToolUseMessage(data.content);
        if (toolInfo) {
          console.log('[AiRequest] 도구 사용 메시지 감지:', toolInfo.name, {
            isCompleted: toolInfo.isCompleted,
            specialType: toolInfo.specialType
          });
          
          // unknown_json 도구는 todo_id가 있는 경우에만 처리
          if (toolInfo.name === 'unknown_json') {
            // todo_id가 없으면 무시
            if (!toolInfo.input.todo_id || typeof toolInfo.input.todo_id !== 'string') {
              console.log('[AiRequest] unknown_json 도구에 todo_id가 없어 무시합니다');
              return;
            }
            console.log('[AiRequest] unknown_json 도구에 todo_id 발견:', toolInfo.input.todo_id);
          }
          
          // 도구 사용 메시지용 특수 도구 상태 추가
          const toolId = toolInfo.name + '_' + Date.now();
          this.activeToolsInfo.set(toolId, {
            name: toolInfo.name,
            input: toolInfo.input,
            isCompleted: toolInfo.isCompleted || false
          });
          
          // 활성 도구 목록 업데이트
          this.updateActiveToolsList({
            name: toolInfo.name,
            input: toolInfo.input,
            isCompleted: toolInfo.isCompleted || false,
            specialType: toolInfo.specialType
          });
          
          // 도구 사용 콜백 호출
          if (this.uiCallbacks.onToolUseReceived) {
            this.uiCallbacks.onToolUseReceived(data.content);
          }
          
          // 도구 사용 시작 이벤트 발생
          if (callback.onToolUseStart) {
            callback.onToolUseStart(toolId, toolInfo.name, toolInfo.input);
            this.hasUsedTool = true;
            this.isFirstChunkAfterTool = true;
          }
          
          return; // 도구 사용 메시지 처리 후 종료
        }
        
        // 문자열이 JSON인지 확인하고 안전하게 파싱 (기존 로직)
        if (typeof data.content === 'string') {
          const parsedContent = JSON.parse(data.content) as Record<string, unknown>;
          
          // 성공 상태를 확인하여 이미 활성화된 도구를 완료 처리
          if (parsedContent.success === true && parsedContent.status === 'OK') {
            console.log('[AiRequest] 성공 상태 감지, 활성 도구 완료 처리:', {
              activeTools: Array.from(this.activeTools),
              lastToolName: this.lastToolName
            });
            
            // 마지막 도구가 있으면 완료 처리
            if (this.lastToolName && callback.onToolUseComplete) {
              console.log('[AiRequest] 마지막 도구 완료 처리:', this.lastToolName);
              callback.onToolUseComplete(this.lastToolName);
              
              // 활성 도구 목록 업데이트
              const existingIndex = this.activeToolsList.findIndex(tool => tool.name === this.lastToolName);
              if (existingIndex !== -1) {
                this.activeToolsList[existingIndex].isCompleted = true;
                // 도구 목록 상태 업데이트 콜백 호출
                if (this.uiCallbacks.onStateUpdate) {
                  this.uiCallbacks.onStateUpdate([...this.activeToolsList]);
                }
              }
            }
            
            // 다음 메시지가 새 메시지로 표시되도록 플래그 설정
            if (this.lastToolName) {
              this.hasUsedTool = true;
              this.isFirstChunkAfterTool = true;
            }
            
            // 도구 사용 메시지로 전달
            if (this.uiCallbacks.onToolUseReceived) {
              this.uiCallbacks.onToolUseReceived(data.content);
            }
            
            return; // 성공 상태 처리 후 종료
          }
          
          // 먼저 시각화 결과인지 확인 (html_base64 또는 png_base64 키가 있는 경우)
          if ('html_base64' in parsedContent || 'png_base64' in parsedContent) {
            isVisualizationResult = true;
            visualizationData = {
              html_base64: parsedContent.html_base64,
              png_base64: parsedContent.png_base64,
              filename: parsedContent.filename || 'chart',
              note: parsedContent.note
            };
            
            console.log('[AiRequest] 시각화 결과 감지:', {
              has_html: !!parsedContent.html_base64,
              has_png: !!parsedContent.png_base64,
              filename: parsedContent.filename || 'chart'
            });
            
            // 툴박스에 시각화 결과 전달
            if (this.uiCallbacks.onToolUseReceived) {
              // 시각화 결과를 특수 형식으로 전달
              const visualizationResultMessage = JSON.stringify(visualizationData);
              
              console.log('[AiRequest] 시각화 결과를 툴박스로 전달:', 
                visualizationResultMessage.substring(0, 100) + 
                (visualizationResultMessage.length > 100 ? '...' : ''));
              
              // 시각화 결과용 특수 도구 상태 추가
              const toolId = 'data_visualization_' + Date.now();
              this.activeToolsInfo.set(toolId, {
                name: 'data_for_visualization',
                input: visualizationData,
                isCompleted: false
              });
              
              // 활성 도구 목록 업데이트
              this.updateActiveToolsList({
                name: 'data_for_visualization',
                input: visualizationData,
                isCompleted: false,
                specialType: 'visualization_result'
              });
              
              this.uiCallbacks.onToolUseReceived(visualizationResultMessage);
              
              // 도구 사용 발생
              this.hasUsedTool = true;
              this.isFirstChunkAfterTool = true;
              
              // 이벤트 디스패치 - 도구 사용 시작 이벤트
              try {
                const toolUseStartEvent = new CustomEvent('tool-use-start', {
                  detail: {
                    toolId,
                    toolName: 'data_for_visualization',
                    input: visualizationData
                  }
                });
                window.dispatchEvent(toolUseStartEvent);
                console.log('[AiRequest] 시각화 결과 도구 사용 시작 이벤트 발생');
              } catch (error) {
                console.error('[AiRequest] 도구 사용 시작 이벤트 발생 실패:', error);
              }
              
              // 곧바로 완료 이벤트도 발생시켜 UI 갱신
              setTimeout(() => {
                if (callback.onToolUseComplete) {
                  console.log('[AiRequest] 시각화 도구 완료 처리: data_for_visualization');
                  callback.onToolUseComplete('data_for_visualization');
                  
                  // 이벤트 디스패치 - 도구 사용 완료 이벤트
                  try {
                    const toolUseCompleteEvent = new CustomEvent('tool-use-complete', {
                      detail: {
                        toolId,
                        toolName: 'data_for_visualization'
                      }
                    });
                    window.dispatchEvent(toolUseCompleteEvent);
                    console.log('[AiRequest] 시각화 결과 도구 사용 완료 이벤트 발생');
                  } catch (error) {
                    console.error('[AiRequest] 도구 사용 완료 이벤트 발생 실패:', error);
                  }
                }
              }, 500);
            }
          }
          
          // 시각화 결과가 아닌 경우, 노트 검색 결과인지 확인
          if (!isVisualizationResult) {
            // results 배열이 있고 rows에 note_id가 포함된 경우 노트 검색 결과로 판단
            const results = parsedContent.results as { rows?: Record<string, unknown>[] }[] | undefined;
            
            if (Array.isArray(results) && results.length > 0) {
              const rows = results[0]?.rows;
              
              if (Array.isArray(rows) && rows.length > 0 && 'note_id' in rows[0]) {
                isNoteResult = true;
                console.log('[AiRequest] 노트 검색 결과 감지:', rows);
                
                // 노트 링크 생성 및 툴박스에 전달
                const noteResults = rows.map(row => {
                  // 안전한 문자열 변환 함수
                  const safeToString = (value: unknown, defaultValue: string): string => {
                    if (value === null || value === undefined) return defaultValue;
                    if (typeof value === 'string') return value;
                    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
                    return defaultValue; // 객체 등 다른 타입은 기본값 반환
                  };
                  
                  const noteId = safeToString(row.note_id, '');
                  const title = safeToString(row.title, '제목 없음');
                  const date = safeToString(row.register_date, '');
                  
                  return {
                    id: noteId,
                    title,
                    date,
                    url: `https://orblit.space/note/${noteId}`
                  };
                });
                
                // 툴박스에 노트 결과 전달
                if (this.uiCallbacks.onToolUseReceived) {
                  // 노트 결과를 특수 형식으로 전달
                  const noteResultMessage = JSON.stringify({
                    type: 'note_search_result',
                    results: noteResults
                  });
                  
                  console.log('[AiRequest] 노트 검색 결과를 툴박스로 전달:', noteResultMessage);
                  
                  // 노트 검색 결과용 특수 도구 상태 추가
                  const toolId = 'search_note_' + Date.now();
                  this.activeToolsInfo.set(toolId, {
                    name: 'search_note',
                    input: { results: noteResults },
                    isCompleted: false
                  });
                  
                  // 활성 도구 목록 업데이트
                  this.updateActiveToolsList({
                    name: 'search_note',
                    input: { results: noteResults },
                    isCompleted: false,
                    specialType: 'note_search_result'
                  });
                  
                  this.uiCallbacks.onToolUseReceived(noteResultMessage);
                  
                  // 도구 사용 발생
                  this.hasUsedTool = true;
                  this.isFirstChunkAfterTool = true;
                  
                  // 이벤트 디스패치 - 도구 사용 시작 이벤트
                  try {
                    const toolUseStartEvent = new CustomEvent('tool-use-start', {
                      detail: {
                        toolId,
                        toolName: 'search_note',
                        input: { results: noteResults }
                      }
                    });
                    window.dispatchEvent(toolUseStartEvent);
                    console.log('[AiRequest] 노트 검색 결과 도구 사용 시작 이벤트 발생');
                  } catch (error) {
                    console.error('[AiRequest] 도구 사용 시작 이벤트 발생 실패:', error);
                  }
                  
                  // 곧바로 완료 이벤트도 발생시켜 UI 갱신
                  setTimeout(() => {
                    if (callback.onToolUseComplete) {
                      console.log('[AiRequest] 노트 검색 도구 완료 처리: search_note');
                      callback.onToolUseComplete('search_note');
                      
                      // 이벤트 디스패치 - 도구 사용 완료 이벤트
                      try {
                        const toolUseCompleteEvent = new CustomEvent('tool-use-complete', {
                          detail: {
                            toolId,
                            toolName: 'search_note'
                          }
                        });
                        window.dispatchEvent(toolUseCompleteEvent);
                        console.log('[AiRequest] 노트 검색 결과 도구 사용 완료 이벤트 발생');
                      } catch (error) {
                        console.error('[AiRequest] 도구 사용 완료 이벤트 발생 실패:', error);
                      }
                    }
                  }, 500);
                }
              }
            }
          }
        }
      } catch (e) {
        // JSON 파싱 실패는 무시 (일반 텍스트로 처리)
        console.log('[AiRequest] 콘텐츠 JSON 파싱 실패 (정상):', e);
      }
    }

    // 응답 완료 처리
    if (data.type === 'end') {
      // 응답 완료 표시
      console.log(`[AiRequest] 응답 완료 ('end' 타입 메시지 수신)`);
      
      // handleEnd 메서드 직접 호출
      this.handleEnd(data);
      
      // 응답 완료 처리 로직 (활성화)
      console.log('[AiRequest] 누적된 텍스트 응답 길이:', this.textResponseBuffer.length);
      
      // 아직 완료되지 않은 도구가 있다면 모두 완료 처리
      if (this.activeTools.size > 0 && this.lastToolName && callback.onToolUseComplete) {
        console.log('[AiRequest] 응답 완료 시 미완료 도구 최종 처리:', 
          Array.from(this.activeTools).join(', '), 
          '마지막 도구:', this.lastToolName);
        
        callback.onToolUseComplete(this.lastToolName);
      }
      
      // 텍스트가 누적되었으면 최종 텍스트 메시지 전송 (도구 사용 후 메시지는 별도의 버블로 표시해야 하므로 비활성화)
      // 완료 시에는 onTextMessage를 호출하지 않음 - 개별 청크 수신 시에만 호출함
      // if (this.textResponseBuffer.length > 0 && callback.onTextMessage) {
      //   console.log('[AiRequest] 완료 시 최종 텍스트 응답 전송', 
      //     this.textResponseBuffer.substring(0, 100) + 
      //     (this.textResponseBuffer.length > 100 ? '...' : ''));
      //   
      //   callback.onTextMessage(this.textResponseBuffer);
      // }
      
      if (callback.onComplete) {
        // 텍스트와 도구 사용 모두 포함된 전체 응답
        const fullResponse = {
          text: this.textResponseBuffer,
          toolUse: this.toolUseResponseBuffer
        };
        
        callback.onComplete(JSON.stringify(fullResponse));
      }
      
      // 버퍼 초기화
      this.responseBuffer = '';
      this.textResponseBuffer = '';
      this.toolUseResponseBuffer = '';
      this.activeRequestId = null;
      this.activeTools.clear();
      this.toolJsonBuffers.clear();
      this.lastToolName = null;
    }
    // 빈 청크 및 done 플래그 감지 시 로그만 남기고 완료 처리는 하지 않음
    else if ((data.type === 'chunk' && data.content === '') || data.done) {
      console.log(`[AiRequest] 빈 청크 또는 done 플래그 감지 (응답은 계속 수신 중)`);
      
      // done 플래그가 있으면 로딩 상태 해제 이벤트 발생
      if (data.done) {
        console.log('[AiRequest] done 플래그 감지, 로딩 상태 해제 이벤트 발생');
        try {
          window.dispatchEvent(new CustomEvent('ai-loading-state-changed', { 
            detail: { isLoading: false }
          }));
        } catch (error) {
          console.error('[AiRequest] 로딩 상태 해제 이벤트 발생 실패:', error);
        }
      }
    }
  }
  
  /**
   * JSON 문자열 파싱을 안전하게 시도하는 유틸리티 함수
   */
  private tryParseJson(jsonString: string): Record<string, unknown> | null {
    try {
      // 빈 문자열이나 빈 객체는 처리하지 않음
      if (!jsonString || jsonString.trim() === '' || jsonString.trim() === '{}') {
        return null;
      }
      
      // 불완전한 JSON 문자열 처리 시도
      let cleanedJson = jsonString.trim();
      
      // 시작 괄호가 없으면 추가
      if (!cleanedJson.startsWith('{')) {
        cleanedJson = '{' + cleanedJson;
      }
      
      // 끝 괄호가 없으면 추가
      if (!cleanedJson.endsWith('}')) {
        cleanedJson = cleanedJson + '}';
      }
      
      // 쉼표로 끝나는 경우 제거
      if (cleanedJson.includes('},}')) {
        cleanedJson = cleanedJson.replace('},}', '}}');
      }
      
      // 파싱 시도
      return JSON.parse(cleanedJson) as Record<string, unknown>;
    } catch (error) {
      // JSON 파싱에 실패했을 때 parseToolUseMessage를 시도
      try {
        console.log('[AiRequest] JSON 파싱 실패, parseToolUseMessage 시도:', jsonString.substring(0, 50) + (jsonString.length > 50 ? '...' : ''));
        const toolInfo = parseToolUseMessage(jsonString);
        if (toolInfo) {
          console.log('[AiRequest] 도구 사용 메시지 감지 (파싱 실패 후):', toolInfo.name);
          // 도구 정보를 일반 객체로 변환하여 반환
          return {
            name: toolInfo.name,
            input: toolInfo.input,
            isCompleted: toolInfo.isCompleted,
            specialType: toolInfo.specialType
          };
        }
      } catch (parseError) {
        console.log('[AiRequest] parseToolUseMessage도 실패:', parseError);
      }
      
      // 파싱 실패 시 null 반환
      return null;
    }
  }

  /**
   * UI 업데이트 콜백을 설정합니다
   * TextInput에서 처리하던 UI 업데이트 로직을 여기서 중앙 관리합니다
   */
  public setUICallbacks(callbacks: AiUICallbacks): void {
    this.uiCallbacks = { ...callbacks };
    console.log('[AiRequest] UI 콜백 설정됨:', Object.keys(callbacks));
  }

  /**
   * 도구 사용 메시지를 처리합니다
   * TextInput의 handleToolUseMessage 함수를 대체합니다
   */
  private handleToolUseMessage(message: string): void {
    console.log(`[AiRequest] 도구 사용 메시지 처리: ${message.substring(0, 50)}...`);
    
    // 노트 검색 결과인지 먼저 확인
    let isNoteSearchResult = false;
    try {
      const data = JSON.parse(message) as Record<string, unknown>;
      if (data && typeof data === 'object' && 'type' in data && data.type === 'note_search_result') {
        isNoteSearchResult = true;
        console.log('[AiRequest] 노트 검색 결과 메시지 감지');
      }
    } catch (e) {
      // JSON 파싱 오류는 무시
    }
    
    // 도구 정보 파싱
    const toolInfo = parseToolUseMessage(message);
    if (toolInfo?.name) {
      console.log('[AiRequest] 도구 사용 정보 파싱 성공:', toolInfo.name, {
        specialType: toolInfo.specialType,
        isCompleted: toolInfo.isCompleted
      });
      
      // unknown_json 도구는 todo_id가 있는 경우에만 처리
      if (toolInfo.name === 'unknown_json') {
        // todo_id가 없으면 무시
        if (!toolInfo.input.todo_id || typeof toolInfo.input.todo_id !== 'string') {
          console.log('[AiRequest] unknown_json 도구에 todo_id가 없어 무시합니다');
          return;
        }
        console.log('[AiRequest] unknown_json 도구에 todo_id 발견:', toolInfo.input.todo_id);
      }
      
      // 도구 사용 메시지 콜백 실행 (UI에 전달)
      if (this.uiCallbacks.onToolUseReceived) {
        this.uiCallbacks.onToolUseReceived(message);
      }
      
      // 도구 목록 업데이트
      this.updateActiveToolsList(toolInfo);
      
      // 노트 검색 결과일 경우 도구 사용 플래그 설정
      if (isNoteSearchResult || toolInfo.specialType === 'note_search_result') {
        this.hasUsedTool = true;
        this.isFirstChunkAfterTool = true;
        console.log('[AiRequest] 노트 검색 결과 감지, 도구 사용 플래그 설정');
      }
    }
  }
  
  /**
   * 활성 도구 목록을 업데이트하고 UI에 알립니다
   */
  private updateActiveToolsList(toolInfo: ToolInfo): void {
    if (!toolInfo.name) return;
    
    // unknown_json 도구는 todo_id가 있는 경우에만 처리
    if (toolInfo.name === 'unknown_json') {
      // todo_id가 없으면 무시
      if (!toolInfo.input.todo_id || typeof toolInfo.input.todo_id !== 'string') {
        console.log('[AiRequest] updateActiveToolsList: unknown_json 도구에 todo_id가 없어 무시합니다');
        return;
      }
      console.log('[AiRequest] updateActiveToolsList: unknown_json 도구에 todo_id 발견:', toolInfo.input.todo_id);
    }
    
    // 이미 동일한 이름의 도구가 있는지 확인
    const existingIndex = this.activeToolsList.findIndex(tool => tool.name === toolInfo.name);
    
    if (existingIndex !== -1) {
      // 기존 도구 업데이트
      this.activeToolsList = this.activeToolsList.map((tool, index) =>
        index === existingIndex ? { ...tool, ...toolInfo } : tool
      );
    } else if (toolInfo.name !== 'unknown_tool') {
      // 새 도구 추가 (이름이 유효한 경우만)
      this.activeToolsList.push({ ...toolInfo, isCompleted: false });
    }
    
    // 도구 목록 상태 업데이트 콜백 호출
    if (this.uiCallbacks.onStateUpdate) {
      this.uiCallbacks.onStateUpdate([...this.activeToolsList]);
    }
  }
  
  /**
   * 도구 완료 상태를 업데이트합니다
   */
  private updateToolCompletion(toolName: string): void {
    // 도구 완료 상태 업데이트
    this.activeToolsList = this.activeToolsList.map(tool =>
      tool.name === toolName ? { ...tool, isCompleted: true } : tool
    );
    
    // 도구 목록 상태 업데이트 콜백 호출
    if (this.uiCallbacks.onStateUpdate) {
      this.uiCallbacks.onStateUpdate([...this.activeToolsList]);
    }
  }

  /**
   * 액티브 도구 목록을 가져옵니다
   */
  public getActiveTools(): ToolInfo[] {
    return [...this.activeToolsList];
  }
  
  /**
   * 액티브 도구 목록을 초기화합니다
   */
  public clearActiveTools(): void {
    this.activeToolsList = [];
    this.activeToolsInfo.clear();
    
    if (this.uiCallbacks.onStateUpdate) {
      this.uiCallbacks.onStateUpdate([]);
    }
  }

  /**
   * 사용자 쿼리로 AI에 메시지를 보내고 UI 업데이트를 처리합니다
   */
  public async sendUserMessage(query: string, userId?: string, userName?: string, timezone?: string, language?: string, toolsString?: string): Promise<string> {
    console.log(`[AiRequest] 사용자 메시지 전송 시작: "${query}"`);
    console.log(`[AiRequest] 사용자 ID: ${userId || '없음'}`);
    console.log(`[AiRequest] 사용자 이름: ${userName || '없음'}`);
    console.log(`[AiRequest] 사용자 타임존: ${timezone || '없음'}`);
    console.log(`[AiRequest] 사용자 언어: ${language || '없음'}`);
    console.log(`[AiRequest] 도구 문자열: ${toolsString || '없음'}`);
    
    // 로딩 상태 설정 이벤트 발생
    try {
      console.log('[AiRequest] 로딩 상태 설정 이벤트 발생');
      window.dispatchEvent(new CustomEvent('ai-loading-state-changed', { 
        detail: { isLoading: true }
      }));
    } catch (error) {
      console.error('[AiRequest] 로딩 상태 설정 이벤트 발생 실패:', error);
    }
    
    // 도구 정보 초기화
    this.clearActiveTools();
    let messageCount = 0;
    
    return this.sendRequest({
      query,
      userId,
      userName,
      timezone,
      language,
      toolsString,
      onTextMessage: (message, isNewMessage) => {
        // 텍스트 메시지 수신 시 처리
        messageCount++;
        console.log(`[AiRequest] 텍스트 청크 #${messageCount} 수신:`, message);
        
        // UI 콜백 호출 - 메시지 버블 업데이트
        if (this.uiCallbacks.onTextUpdate) {
          console.log(`[AiRequest] UI에 텍스트 메시지 전달: ${message.length}자`, isNewMessage ? '(새 메시지)' : '');
          this.uiCallbacks.onTextUpdate(message, isNewMessage);
        }
      },
      onToolUseMessage: (toolMessage) => {
        // 도구 사용 메시지 수신 시 내부 처리 함수 호출
        this.handleToolUseMessage(toolMessage);
      },
      onToolUseStart: (toolId, toolName, input) => {
        console.log(`[AiRequest] 도구 사용 시작: ${toolName}`, {
          toolId, 
          toolName, 
          inputKeys: Object.keys(input || {})
        });
        
        // 도구 이름이 유효한지 확인
        if (!toolName || toolName === 'unknown_tool') {
          console.log('[AiRequest] 유효하지 않은 도구 이름, 무시합니다:', toolName);
          return;
        }
        
        // 도구 정보 저장
        this.activeToolsInfo.set(toolId, {
          name: toolName,
          input,
          isCompleted: false
        });
        
        // 활성 도구 목록 업데이트
        this.updateActiveToolsList({
          name: toolName,
          input,
          isCompleted: false
        });
        
        // UI 콜백 호출 - 도구 사용 시작 처리
        if (this.uiCallbacks.onToolUseStarted) {
          this.uiCallbacks.onToolUseStarted(toolId, toolName, input);
        }
        
        // 이벤트 디스패치 - 도구 사용 시작 이벤트
        try {
          const toolUseStartEvent = new CustomEvent('tool-use-start', {
            detail: {
              toolId,
              toolName,
              input
            }
          });
          window.dispatchEvent(toolUseStartEvent);
          console.log('[AiRequest] 도구 사용 시작 이벤트 발생:', toolName);
        } catch (error) {
          console.error('[AiRequest] 도구 사용 시작 이벤트 발생 실패:', error);
        }
      },
      onToolUseComplete: (toolName) => {
        console.log(`[AiRequest] 도구 사용 완료: ${toolName}`);
        
        // 도구 완료 상태 업데이트
        this.updateToolCompletion(toolName);
        
        let completedToolId = '';
        
        // 도구 ID 찾기
        for (const [id, info] of this.activeToolsInfo.entries()) {
          if (info.name === toolName) {
            completedToolId = id;
            this.activeToolsInfo.set(id, { ...info, isCompleted: true });
            break;
          }
        }
        
        if (completedToolId) {
          // UI 콜백 호출 - 도구 사용 완료 처리
          if (this.uiCallbacks.onToolUseCompleted) {
            this.uiCallbacks.onToolUseCompleted(completedToolId, toolName);
          }
          
          // 이벤트 디스패치 - 도구 사용 완료 이벤트
          try {
            const toolUseCompleteEvent = new CustomEvent('tool-use-complete', {
              detail: {
                toolId: completedToolId,
                toolName
              }
            });
            window.dispatchEvent(toolUseCompleteEvent);
            console.log('[AiRequest] 도구 사용 완료 이벤트 발생:', toolName);
          } catch (error) {
            console.error('[AiRequest] 도구 사용 완료 이벤트 발생 실패:', error);
          }
        }
      },
      onComplete: (fullResponse) => {
        // 응답 완료 처리
        console.log(`[AiRequest] AI 응답 완료 (총 ${messageCount}개 메시지)`);
        
        // 모든 활성 도구를 완료 상태로 설정
        this.activeToolsList = this.activeToolsList.map(tool => ({
          ...tool,
          isCompleted: true
        }));
        
        // 도구 목록 상태 업데이트 콜백 호출
        if (this.uiCallbacks.onStateUpdate) {
          this.uiCallbacks.onStateUpdate([...this.activeToolsList]);
        }
        
        // 모든 활성 도구를 완료 상태로 설정
        for (const [id, info] of this.activeToolsInfo.entries()) {
          if (!info.isCompleted) {
            this.activeToolsInfo.set(id, { ...info, isCompleted: true });
            
            // UI 콜백 호출 - 완료되지 않은 도구 완료 처리
            if (this.uiCallbacks.onToolUseCompleted) {
              this.uiCallbacks.onToolUseCompleted(id, info.name);
            }
          }
        }
        
        return fullResponse;
      },
      onError: (error) => {
        console.error(`[AiRequest] AI 요청 오류:`, error);
      }
    });
  }

  /**
   * 응답 완료 메시지를 처리합니다
   */
  private handleEnd(data: ServerResponse): void {
    try {
      console.log('[AiRequestManager] 응답 완료:', data);
      
      if (!this.activeRequestId) {
        console.warn('[AiRequestManager] 활성 요청 ID가 없는 상태에서 응답 완료 수신');
        return;
      }
      
      // 콜백 정보 조회
      const callback = this.callbacks.get(this.activeRequestId);
      if (!callback) {
        console.warn('[AiRequestManager] 콜백이 설정되지 않은 요청:', this.activeRequestId);
        return;
      }
      
      // 완료 콜백 호출
      if (callback.onComplete) {
        callback.onComplete(this.responseBuffer);
      }
      
      // 로딩 상태 해제 이벤트 발생
      console.log('[AiRequestManager] 응답 완료, 로딩 상태 해제 이벤트 발생');
      try {
        window.dispatchEvent(new CustomEvent('ai-loading-state-changed', { 
          detail: { isLoading: false }
        }));
      } catch (error) {
        console.error('[AiRequestManager] 로딩 상태 해제 이벤트 발생 실패:', error);
      }
      
      // 요청 정보 정리
      this.callbacks.delete(this.activeRequestId);
      this.activeRequestId = null;
      this.responseBuffer = '';
      
      // 도구 목록 초기화 여부 확인 (완전히 비울지는 상황에 따라 결정)
      // this.clearActiveTools();
      
      console.log('[AiRequestManager] 응답 완료 처리 완료');
    } catch (error) {
      console.error('[AiRequestManager] 응답 완료 처리 오류:', error);
    }
  }
}

export { AiRequestManager };
export type { AiRequestOptions, MessageContent, TextContent, ToolUseContent, AiUICallbacks };

// 기본 인스턴스 생성 및 내보내기는 AiRequestManager.ts로 이동
// export const defaultAiRequestManager = new AiRequestManager();

// Fast refresh 규칙을 위해 분리된 기본 내보내기
export default AiRequestManager;
